import React from 'react';
import _ from 'lodash';

import { WorkerInputText } from '../WorkerInputText.jsx';
import { DateInput } from '../../DateInput';
import {
  SEX_TYPE,
  WORKER_PAGE_STATUS,
  DIALOG_STATUS,
  TAB_KEY_STATUS,
} from '../../../utilities/constants';
import {
  FormElement,
  FormListItem,
  FormListSubHeader,
} from '../../CommonFormComponents';
import {
  HEAD_OFFICE_OPTION,
  PartnerCompanyBranchDropdown,
} from '../../../components/PartnerCompanyBranchDropdown';
import { RadioButtonIcon } from '../../icons/RadioButtonIcon';
import SessionStorageService from '../../../services/utilities/SessionStorageService.js';

/**
 * 性別選択
 * @param {string} selectValue
 * @param {function} onChange
 * */
const SexInput = ({ selectValue, onChange }) => {
  return (
    <div className='form-list-radio'>
      {_.map(SEX_TYPE, (type, index) => {
        return (
          <div key={index} className='form-list-radio__item'>
            <label htmlFor={type.Name} className='radio' key={type.Code}>
              <input
                type='radio'
                id={type.Name}
                name='sex'
                value={type.Code}
                checked={selectValue === type.Code}
                onChange={onChange}
              />
              <RadioButtonIcon className='icon--2x' />
              <span>{type.DisplayName}</span>
            </label>
          </div>
        );
      })}
    </div>
  );
};

/**
 * 性別名称を取得
 * @param {string} sexCode: 1:男性、2:女性、3:その他
 * @return {string} 性別名
 * */
const getSexName = sexCode => {
  const viewType = _.filter(SEX_TYPE, type => {
    return type.Code === sexCode;
  });
  if (viewType !== null && viewType !== undefined && viewType.length > 0) {
    return viewType[0].DisplayName;
  }
  // 性別が設定されていない場合はデフォルトで男性を表示する
  return SEX_TYPE.Man.DisplayName;
};

/**
 * 作業員編集 画面 > 基本情報
 * @param {object} formValues: 表示したいデータ群
 * @param {object} messages: バリデーションエラーメッセージ群
 * @param {boolean} disabledAll: 項目を編集することができるかどうか？
 * @param {function} events: ファンクション群(setValue, validate)
 */
export const WorkerInfoForm = ({
  formValues,
  messages,
  disabledAll,
  pageStatus,
  events,
  isManagerPermission,
  dialogStatus,
}) => {
  const companyId = SessionStorageService.getSessionSelectedPartnerCompanyId();
  const isNewView = pageStatus === WORKER_PAGE_STATUS.NEW;

  const workerInputTextOnChange = e => {
    events.setValue(e);
  };

  const workerInputTextOnBlur = e => {
    events.validate(e.target.name);
  };

  const handlePartnerCompanyBranchDropdownChange = id => {
    const value = {
      target: {
        name: 'partner_company_branch',
        value: {
          id,
        },
      },
    };
    events.setValue(value);
  };

  return (
    <React.Fragment>
      <FormListSubHeader title='基本情報' noMarginTop={true} />
      {(!isNewView || isManagerPermission) && (
        <FormListItem
          label='支店'
          required={disabledAll === false && isManagerPermission}
        >
          <FormElement info='支店のデフォルトは本社となります。支店の登録、支店への配属は企業管理者のみが実行できます。'>
            <PartnerCompanyBranchDropdown
              selectedCompanyId={companyId}
              selectedPartnerCompanyBranchId={_.get(
                formValues,
                'partner_company_branch.id',
                HEAD_OFFICE_OPTION.id
              )}
              onSelect={handlePartnerCompanyBranchDropdownChange}
              previewValue={_.get(
                formValues,
                'partner_company_branch.name',
                HEAD_OFFICE_OPTION.name
              )}
              disabled={disabledAll || !isManagerPermission}
              fixedOptions={[HEAD_OFFICE_OPTION]}
            />
          </FormElement>
        </FormListItem>
      )}
      <FormListItem label='姓' required={disabledAll === false}>
        <FormElement validate={messages.name_sei}>
          <WorkerInputText
            name='name_sei'
            value={formValues.name_sei}
            disabled={disabledAll}
            onChange={workerInputTextOnChange}
            onBlur={workerInputTextOnBlur}
            tabKeyStatus={
              dialogStatus === DIALOG_STATUS.disabled.id
                ? TAB_KEY_STATUS.enabled
                : TAB_KEY_STATUS.disabled
            }
          />
        </FormElement>
      </FormListItem>
      <FormListItem label='名' required={disabledAll === false}>
        <FormElement validate={messages.name_mei}>
          <WorkerInputText
            name='name_mei'
            value={formValues.name_mei}
            disabled={disabledAll}
            onChange={workerInputTextOnChange}
            onBlur={workerInputTextOnBlur}
            tabKeyStatus={
              dialogStatus === DIALOG_STATUS.disabled.id
                ? TAB_KEY_STATUS.enabled
                : TAB_KEY_STATUS.disabled
            }
          />
        </FormElement>
      </FormListItem>
      <FormListItem label='セイ' required={disabledAll === false}>
        <FormElement validate={messages.name_sei_kana}>
          <WorkerInputText
            name='name_sei_kana'
            value={formValues.name_sei_kana}
            messages={messages}
            disabled={disabledAll}
            onChange={workerInputTextOnChange}
            onBlur={workerInputTextOnBlur}
            tabKeyStatus={
              dialogStatus === DIALOG_STATUS.disabled.id
                ? TAB_KEY_STATUS.enabled
                : TAB_KEY_STATUS.disabled
            }
          />
        </FormElement>
      </FormListItem>
      <FormListItem label='メイ' required={disabledAll === false}>
        <FormElement validate={messages.name_mei_kana}>
          <WorkerInputText
            name='name_mei_kana'
            value={formValues.name_mei_kana}
            messages={messages}
            disabled={disabledAll}
            onChange={workerInputTextOnChange}
            onBlur={workerInputTextOnBlur}
            tabKeyStatus={
              dialogStatus === DIALOG_STATUS.disabled.id
                ? TAB_KEY_STATUS.enabled
                : TAB_KEY_STATUS.disabled
            }
          />
        </FormElement>
      </FormListItem>
      <FormListItem label='性別' required={disabledAll === false}>
        <FormElement validate={messages.sex}>
          {disabledAll === false ? (
            <SexInput selectValue={formValues.sex} onChange={events.setValue} />
          ) : (
            <WorkerInputText
              name='sex'
              value={getSexName(formValues.sex)}
              disabled={disabledAll}
              onChange={workerInputTextOnChange}
              onBlur={workerInputTextOnBlur}
              tabKeyStatus={
                dialogStatus === DIALOG_STATUS.disabled.id
                  ? TAB_KEY_STATUS.enabled
                  : TAB_KEY_STATUS.disabled
              }
            />
          )}
        </FormElement>
      </FormListItem>
      <FormListItem label='生年月日' required={disabledAll === false}>
        <DateInput
          dateString={formValues.birthday}
          disabled={disabledAll}
          onChangeValue={events.setCalendar}
          isPreview={disabledAll}
          message={messages.birthday}
          onBlur={() => workerInputTextOnBlur({ target: { name: 'birthday' } })}
          jpDelimiterChar={true}
          showPlaceholder={true}
          tabKeyStatus={
            dialogStatus === DIALOG_STATUS.disabled.id
              ? TAB_KEY_STATUS.enabled
              : TAB_KEY_STATUS.disabled
          }
        />
      </FormListItem>
    </React.Fragment>
  );
};



// WEBPACK FOOTER //
// ./src/components/worker/form/WorkerInfoForm.jsx